import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import api, { getApiErrorMessage } from '../services/api';
import { ManagementRequestResponse } from '../types/index';

const CompanyRegistration: React.FC = () => {
  const { storeId, currentUser } = useAuth();
  const { managementToken, managementAccount, refreshAccount } = useManagementAuth();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [businessType, setBusinessType] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingRequests, setExistingRequests] = useState<ManagementRequestResponse[]>([]);

  // 既存の申請を確認
  useEffect(() => {
    const fetchRegistrations = async () => {
      const email = managementAccount?.email || currentUser?.email;
      if (!managementToken && !email) return;
      setLoading(true);
      try {
        const data = await api.managementCompany.getMyRegistrations(
          managementToken ? { token: managementToken } : { email: email! }
        );
        setExistingRequests(data);
      } catch {
        // 取得失敗は無視（初回申請のユーザー）
      } finally {
        setLoading(false);
      }
    };
    fetchRegistrations();
  }, [managementToken, currentUser]);

  // 既に企業に紐付いている管理アカウントの場合は管理画面へ誘導
  const hasCompany = managementAccount?.companies && managementAccount.companies.length > 0;
  if (hasCompany) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 2 }}>
        <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
          既に企業として登録済みです。
        </Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/management')}>
            企業管理画面へ
          </Button>
        </Box>
      </Box>
    );
  }

  const pendingRequest = existingRequests.find(r => r.status === 'pending');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!managementToken && !currentUser?.email) {
      setError('認証情報が取得できません。再ログインしてください。');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const authOptions = managementToken
        ? { token: managementToken }
        : {};

      await api.managementCompany.register({
        request_type: 'company_registration',
        company_name: companyName,
        company_address: address,
        company_phone: phone,
        representative_name: representativeName,
        business_type: businessType || undefined,
        store_id: storeId || undefined,
        applicant_email: managementAccount?.email || currentUser?.email || undefined,
      }, authOptions);

      // 申請成功 → 店舗設定画面へ遷移（リロード）
      navigate('/settings/store');
      return;
    } catch (err: any) {
      const msg = getApiErrorMessage(err, '企業登録申請に失敗しました。');
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip icon={<HourglassEmptyIcon />} label="審査中" color="warning" size="small" />;
      case 'approved':
        return <Chip icon={<CheckCircleOutlineIcon />} label="承認済み" color="success" size="small" />;
      case 'rejected':
        return <Chip icon={<CancelIcon />} label="却下" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 2, p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon /> 企業登録申請
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        企業として登録すると、複数の店舗を一括管理できるようになります。
        申請は運営チームの審査後に承認されます。
      </Typography>

      {/* 既存の申請一覧 */}
      {existingRequests.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="申請履歴" titleTypographyProps={{ variant: 'subtitle1' }} />
          <CardContent>
            {existingRequests.map((req) => (
              <Box key={req.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">{req.company_name}</Typography>
                  {getStatusChip(req.status)}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  申請日: {new Date(req.created_at).toLocaleDateString('ja-JP')}
                </Typography>
                {req.reject_reason && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    却下理由: {req.reject_reason}
                  </Alert>
                )}
                {req.status === 'approved' && (
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={async () => {
                      await refreshAccount();
                      navigate('/management');
                    }}>
                      企業管理画面へ
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* 審査中の申請がある場合は新規申請を制限 */}
      {pendingRequest ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          現在、企業登録申請が審査中です。結果をお待ちください。
        </Alert>
      ) : (
        <Card>
          <CardHeader
            title="企業情報入力"
            titleTypographyProps={{ variant: 'subtitle1' }}
            avatar={<BusinessIcon color="primary" />}
          />
          <Divider />
          <CardContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <form onSubmit={handleSubmit}>
              <TextField
                label="企業名"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="住所"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="電話番号"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="代表者名"
                value={representativeName}
                onChange={(e) => setRepresentativeName(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="業種（任意）"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                fullWidth
                margin="normal"
              />

              {storeId && (
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  現在ご利用中の店舗が企業の最初の店舗として紐付けられます。
                </Alert>
              )}

              <Box sx={{ mt: 3, textAlign: 'right' }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting || !companyName || !address || !phone || !representativeName}
                >
                  {submitting ? <CircularProgress size={24} /> : '申請を送信'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CompanyRegistration;
